import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import {
  CartBrowsingCriteria,
  Portfolio,
  BrowsingInfo,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { INITIAL_BROWSING_CRITERIA } from 'store/domain/cartStoreV2';
import { Cart } from 'types/cartServiceV2';
import { CustomPlanRateCapFields } from 'store/ui-stores/proposal-v2/customPlanRateCapStore';
import { PortfolioToCartItem } from 'store/modules/proposal/types';
import { addCustomRates } from 'api/operations/productServiceV2';
import { getRateRfqForm } from 'api/operations/formService';
import { INITIAL_FORM_OBJECT } from '../elections/editPrimaryModalStore';

export default class RateRFQModalStore {
  rootStore: RootStore;
  loading = false;
  showRateRfqModal = false;
  form: FormObjectFromJSON;
  dynamicForm: null | formUtils.DynamicFormController;
  bcIndex = 0;
  portfolio: Portfolio | null = null;
  portfolioBrowsingCriteria: CartBrowsingCriteria;

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  async openRateRFQPlanModal(
    formId: string,
    { portfolio, cartItem }: PortfolioToCartItem,
    lastUpdateBrowsingCriteria: CartBrowsingCriteria,
    bcIndex: number,
    modalType: 'ADD' | 'UPDATE' = 'ADD',
  ): Promise<void> {
    this.bcIndex = bcIndex;
    this.portfolio = portfolio;
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;
    this.showRateRfqModal = true;
    this.setLoading(true);

    const { id, name, products } = portfolio;
    const convertedProducts = products.map((product) => {
      return { productId: product.imProductId, productName: product.productDisplayName };
    });

    const form = await getRateRfqForm(formId, id, name, convertedProducts, this.rootStore.userStore.sponsorId);
    runInAction(() => {
      this.form = form.data.getRateRfqForm;
      // Newly added bcIndexes will be absent
      const bc = cartItem?.browsingCriteria[bcIndex] || lastUpdateBrowsingCriteria;
      if (bc) {
        this.portfolioBrowsingCriteria = bc;
        // Do not populate form for new version
        this.createFormController(modalType === 'ADD' ? {} : bc[portfolio.productLineId] || {});
      }
      this.setLoading(false);
    });
  }

  createFormController(answers: Record<string, string>): void {
    this.setLoading(true);
    this.dynamicForm = new formUtils.DynamicFormController<CustomPlanRateCapFields>(
      this.form,
      this.rootStore.userStore.userRoles,
    );
    this.dynamicForm.populateFields(answers);
    if (answers.custom) this.dynamicForm.populateCustomFields(JSON.parse(answers.custom) as Record<string, string>);
    this.setLoading(false);
  }

  public async saveProposalRatesAnswers(cartId: string): Promise<Cart | null> {
    if (!this.dynamicForm || !this.portfolio) return null;
    const convertedPortfolio = toJS(this.portfolio);
    const answersFromForm = this.dynamicForm.getCanonicalAnswers() as BrowsingInfo;

    this.portfolioBrowsingCriteria[convertedPortfolio.productLineId] = {
      ...answersFromForm,
      custom: JSON.stringify(this.dynamicForm.getCustomFormFieldsObject()),
    };

    // Add custom rates
    await addCustomRates(cartId, convertedPortfolio.id, this.bcIndex, answersFromForm);

    try {
      const updatedCard = await this.rootStore.cartStoreV2.addPortfolioToCart({
        cartId,
        portfolioId: convertedPortfolio.id,
        portfolioName: convertedPortfolio.name,
        browsingCriteria: this.portfolioBrowsingCriteria,
        productLineId: convertedPortfolio.productLineId,
        bcIndex: this.bcIndex,
      });
      return updatedCard?.data?.addPortfolioConfigToCart || null;
    } catch (error) {
      this.rootStore.proposalItemsStore.setPortfolioLoadingMapping({
        ...this.rootStore.proposalItemsStore.portfolioLoading,
        [convertedPortfolio.id]: false,
      });
      return null;
    }
  }

  closeRateRFQModalModal(): void {
    this.showRateRfqModal = false;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;
    this.portfolioBrowsingCriteria = INITIAL_BROWSING_CRITERIA;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
